import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../../lib/api-client";
import type { Seat } from "./useSeats";

export interface Reservation {
  date: string;
  seatId: number;
  userEmail: string;
  userName: string;
  repeatId?: string;
  createdAt: string;
  isOwn?: boolean;
  seat?: Seat;
}

export interface Repeat {
  repeatId: string;
  userEmail: string;
  seatId: number;
  startDate: string;
  endDate?: string;
  daysOfWeek: number[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Issue #102: スキップ理由を構造化。POST レスポンス由来は 2 値、GET レスポンス由来は
// `unknown` を含む 3 値。Frontend の型は core の型と並走して定義する（既存 `Repeat` と
// 同様にフロントエンドローカル定義を維持）。
export type SkippedReasonAtCreation = "user_already_booked" | "seat_taken";
export type SkippedReasonAtView =
  | "user_already_booked"
  | "seat_taken"
  | "unknown";

export interface SkippedDateAtCreation {
  date: string;
  reason: SkippedReasonAtCreation;
}

export interface SkippedDateAtView {
  date: string;
  reason: SkippedReasonAtView;
}

interface ReservationsResponse {
  date: string;
  reservations: Reservation[];
}

interface MyReservationsResponse {
  reservations: Reservation[];
}

interface CreateRecurringResponse {
  repeat: Repeat;
  createdCount: number;
  skippedCount: number;
  skippedDates: SkippedDateAtCreation[];
}

interface SkippedDatesResponse {
  repeatId: string;
  skippedDates: SkippedDateAtView[];
}

function invalidateReservationsByDate(
  queryClient: QueryClient,
  date: string
): void {
  queryClient.invalidateQueries({ queryKey: ["reservations", date] });
  queryClient.invalidateQueries({ queryKey: ["reservations", "me"] });
  // 繰り返し予約関連のキャッシュも無効化（スキップ詳細が自己治癒で変わる可能性）
  queryClient.invalidateQueries({ queryKey: ["reservations", "recurring"] });
}

// 意図的に広い無効化: 繰り返し予約の作成・削除は複数日付の予約に影響するため、
// 個別キー指定では漏れが生じる。["reservations"] プレフィックス一括無効化で
// 日付別・自分の予約・繰り返し一覧・スキップ詳細をすべてリフレッシュする。
function invalidateAllReservations(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ["reservations"] });
}

export function useReservations(date: string | undefined) {
  return useQuery({
    queryKey: ["reservations", date],
    queryFn: async () => {
      if (!date) {
        return [];
      }
      const response = await apiGet<ReservationsResponse>("/reservations", {
        date,
      });
      return response.reservations;
    },
    enabled: Boolean(date),
  });
}

export function useMyReservations(from?: string, to?: string) {
  return useQuery({
    queryKey: ["reservations", "me", from, to],
    queryFn: async () => {
      const response = await apiGet<MyReservationsResponse>(
        "/reservations/me",
        { from, to }
      );
      return response.reservations;
    },
  });
}

export interface RepeatWithSeat extends Repeat {
  seat: { seatId: number; label: string } | null;
}

interface MyRepeatsResponse {
  repeats: RepeatWithSeat[];
}

export function useMyRepeats() {
  return useQuery({
    queryKey: ["reservations", "recurring"],
    queryFn: async () => {
      const response = await apiGet<MyRepeatsResponse>(
        "/reservations/recurring"
      );
      return response.repeats;
    },
  });
}

/**
 * 指定 `repeatId` に紐付くスキップ日付を動的算出で取得する (Issue #102)。
 *
 * - バックエンドが JST 0:00 基準で過去日を除外して返す
 * - 自己治癒（同じ座席に再予約）された日付は一覧から消える
 * - `repeatId` が `undefined` の場合はクエリを無効化（カードの展開後のみ fetch する想定）
 */
export function useRepeatSkippedDates(repeatId: string | undefined) {
  return useQuery({
    queryKey: ["reservations", "recurring", repeatId, "skipped-dates"],
    queryFn: async () => {
      if (!repeatId) {
        return [];
      }
      const response = await apiGet<SkippedDatesResponse>(
        `/reservations/recurring/${repeatId}/skipped-dates`
      );
      return response.skippedDates;
    },
    enabled: Boolean(repeatId),
  });
}

export function useCreateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      date: string;
      seatId: number;
      // 管理者のみ指定可能。指定時は対象ユーザー名義で代行予約を作成する。
      targetUserEmail?: string;
    }) => apiPost<Reservation>("/reservations", data),
    onSuccess: (_, variables) => {
      invalidateReservationsByDate(queryClient, variables.date);
    },
  });
}

export function useCancelReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { date: string; seatId: number }) =>
      apiDelete<void>(`/reservations/${data.date}/${data.seatId}`),
    onSuccess: (_, variables) => {
      invalidateReservationsByDate(queryClient, variables.date);
    },
  });
}

export function useCreateRecurringReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      startDate: string;
      endDate?: string;
      daysOfWeek: number[];
      seatId: number;
    }) => apiPost<CreateRecurringResponse>("/reservations/recurring", data),
    onSuccess: () => {
      invalidateAllReservations(queryClient);
    },
  });
}

export function useCancelRecurringReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { repeatId: string; date?: string }) => {
      const endpoint = data.date
        ? `/reservations/recurring/${data.repeatId}?date=${data.date}`
        : `/reservations/recurring/${data.repeatId}`;
      return apiDelete<void>(endpoint);
    },
    onSuccess: () => {
      invalidateAllReservations(queryClient);
    },
  });
}
